import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/Container";
import { ArrowLeft, ArrowRight, Calendar, User } from "lucide-react";
import { articles } from "@/data/articles";
import { usePageTitle } from "@/hooks/usePageTitle";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formRateLimiter } from "@/lib/utils/rateLimit";
import { 
  validateSubscriberForm, 
  type SubscriberFormData, 
  type ValidationResult,
  type ValidationError,
  type ValidationFailure 
} from "@/lib/utils/formValidation";

const initialFormData: SubscriberFormData = {
  email: ""
};

const isValidationFailure = (result: ValidationResult): result is ValidationFailure => {
  return !result.success;
};

const NewsDetailPage = () => {

  const { newsId } = useParams();
  
  // Находим нужную статью по ID
  const article = articles.find((art) => art.id === newsId);
  usePageTitle(article?.title || "Статья не найдена");
  
  // Находим предыдущую и следующую статьи по дате
  const relatedArticles = article
    ? articles
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .reduce((acc, art) => {
          if (art.id === article.id) {
            const currentIndex = articles.indexOf(art);
            const prevArticle = articles[currentIndex - 1];
            const nextArticle = articles[currentIndex + 1];
            
            if (prevArticle && nextArticle) {
              return [prevArticle, nextArticle];
            } else if (prevArticle) {
              return [articles[currentIndex - 2], prevArticle];
            } else if (nextArticle) {
              return [nextArticle, articles[currentIndex + 2]];
            }
          }
          return acc;
        }, [])
        .slice(0, 2)
    : [];

  const [formData, setFormData] = useState<SubscriberFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [remainingTime, setRemainingTime] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear validation error for the field being changed
    setValidationErrors(prev => prev.filter(error => error.field !== name));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check rate limiting
    if (!formRateLimiter.canSubmit("subscriber")) {
      const time = formRateLimiter.getRemainingTime("subscriber");
      setRemainingTime(time);
      toast.error(`Пожалуйста, подождите ${Math.ceil(time / 1000)} секунд перед повторной отправкой`);
      return;
    }

    // Validate form data
    const result = validateSubscriberForm(formData);
    if (isValidationFailure(result)) {
      setValidationErrors(result.errors);
      toast.error("Пожалуйста, исправьте ошибки в форме");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await api.subscribers.subscribe(result.data.email);
      
      // Send notification to Telegram with the current URL
      try {
        await api.subscribers.notifyTelegram(result.data.email);
      } catch (telegramError) {
        console.error("Error sending Telegram notification:", telegramError);
        // Don't show error to user, just log it
      }
      
      setIsSubmitted(true);
      setFormData(initialFormData);
      
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (error: any) {
      console.error("Error subscribing:", error);
      toast.error(error.message || "Произошла ошибка при оформлении подписки. Пожалуйста, попробуйте позже.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!article) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-20">
          <Container>
            <div className="py-32 text-center">
              <h1 className="text-3xl font-bold mb-6">Статья не найдена</h1>
              <Link to="/news" className="button-primary">
                Вернуться к списку новостей
              </Link>
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-secondary">
          <Container>
            <Link to="/news" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Назад к новостям
            </Link>
            
            <div className="max-w-4xl mx-auto">
              <div className="tag mb-4 animate-fade-in">{article.category}</div>
              <h1 className="heading text-3xl md:text-4xl font-bold mb-6 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                {article.title}
              </h1>
              
              <div className="flex flex-wrap items-center text-muted-foreground mb-8 animate-fade-in" style={{ animationDelay: "0.3s" }}>
                <div className="flex items-center mr-6 mb-2">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>{article.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
                <div className="flex items-center mb-2">
                  <User className="h-4 w-4 mr-2" />
                  <span>{article.author}</span>
                </div>
              </div>
              
              <div className="relative mb-10 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                <img 
                  src={article.image}
                  alt={article.title}
                  className="w-full h-auto rounded-xl object-cover"
                />
              </div>
            </div>
          </Container>
        </section>

        {/* Article Content */}
        <section className="py-10">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-8">
                <article className="prose prose-lg dark:prose-invert max-w-none">
                  <div 
                    className="prose-headings:font-display prose-headings:font-bold prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-img:shadow-lg"
                    dangerouslySetInnerHTML={{ __html: article.content }} 
                  />
                </article>
                
                <div className="flex flex-wrap items-center justify-between mt-10 pt-6 border-t border-border">
                  <div>
                    <span className="text-sm text-muted-foreground">Поделиться:</span>
                    <div className="flex space-x-4 mt-2">
                      <button 
                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary hover:bg-primary/10 transition-colors"
                        aria-label="Поделиться в Facebook"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                        </svg>
                      </button>
                      <button 
                        onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}`, '_blank')}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary hover:bg-primary/10 transition-colors"
                        aria-label="Поделиться в Twitter"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
                        </svg>
                      </button>
                      <button 
                        onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(article.title)}`, '_blank')}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary hover:bg-primary/10 transition-colors"
                        aria-label="Поделиться в LinkedIn"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                          <rect width="4" height="12" x="2" y="9"></rect>
                          <circle cx="4" cy="4" r="2"></circle>
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <Link to="/contact" className="button-secondary mt-4 sm:mt-0">
                    Связаться с нами
                  </Link>
                </div>
              </div>
              
              <div className="lg:col-span-4">
                <div className="sticky top-24">
                  <div className="glass rounded-xl p-6 mb-8">
                    <h3 className="text-xl font-semibold mb-4">Другие статьи</h3>
                    <div className="space-y-4">
                      {relatedArticles.map((relatedArticle) => (
                        <div key={relatedArticle.id} className="group">
                          <Link to={`/news/${relatedArticle.id}`} className="block">
                            <div className="tag mb-1">{relatedArticle.category}</div>
                            <h4 className="font-medium group-hover:text-primary transition-colors">
                              {relatedArticle.title}
                            </h4>
                            <div className="text-sm text-muted-foreground mt-1">{relatedArticle.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                          </Link>
                        </div>
                      ))}
                    </div>
                    <Link 
                      to="/news" 
                      className="inline-flex items-center text-primary font-medium mt-4"
                    >
                      Все новости
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 border border-border">
                    <h3 className="text-xl font-semibold mb-4">Подписаться на новости</h3>
                    <p className="text-muted-foreground mb-4 text-sm">
                      Получайте последние новости и обновления продуктов прямо на вашу почту
                    </p>
                    
                    {isSubmitted ? (
                      <div className="text-center py-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <h3 className="text-xl font-semibold mb-2">Подписка оформлена!</h3>
                        <p className="text-muted-foreground">
                          Спасибо за подписку. Теперь вы будете получать наши новости.
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Ваш email"
                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                              validationErrors.some(e => e.field === "email") ? "border-red-500" : "border-border"
                            }`}
                          />
                          {validationErrors.some(e => e.field === "email") && (
                            <p className="mt-1 text-sm text-red-500">
                              {validationErrors.find(e => e.field === "email")?.message}
                            </p>
                          )}
                        </div>
                        <button 
                          type="submit" 
                          disabled={isSubmitting || remainingTime > 0}
                          className="button-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <span className="flex items-center justify-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Отправка...
                            </span>
                          ) : remainingTime > 0 ? (
                            <span className="flex items-center justify-center">
                              Подождите {Math.ceil(remainingTime / 1000)} сек
                            </span>
                          ) : (
                            <span className="flex items-center justify-center">
                              Подписаться
                            </span>
                          )}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-secondary">
          <Container>
            <div className="glass rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="heading text-3xl font-bold mb-4">
                    Узнайте больше о наших продуктах
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Ознакомьтесь с нашими инновационными решениями для бизнеса или свяжитесь с нами для получения консультации по внедрению.
                  </p>
                  <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                    <Link to="/products" className="button-primary">
                      Наши продукты
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                    <Link to="/contact" className="button-secondary">
                      Связаться с нами
                    </Link>
                  </div>
                </div>
                <div className="relative">
                  <div className="aspect-square max-w-xs mx-auto bg-white/5 rounded-2xl overflow-hidden p-6">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1591017403286-fd8493524e1e?q=80&w=2070')] bg-cover bg-center opacity-10 rounded-2xl"></div>
                    <div className="relative h-full flex flex-col items-center justify-center text-center space-y-4">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                          <rect width="20" height="14" x="2" y="5" rx="2"></rect>
                          <line x1="2" x2="22" y1="10" y2="10"></line>
                        </svg>
                      </div>
                      <h3 className="text-2xl font-semibold">Демонстрация продуктов</h3>
                      <p className="text-muted-foreground">
                        Запросите демонстрацию наших продуктов и узнайте, как они могут решить задачи вашего бизнеса
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NewsDetailPage;
